/**
 * Allocation of ids and timestamps on a draft.
 *
 * Object ids are a **keyed permutation of a counter**: `clock.nextObjectId` counts allocations and a
 * 32-bit Feistel network keyed by the match seed (the `ids` RNG words, never advanced) maps the count to
 * an opaque id. Ids are therefore unique for the whole game (a stale reference, such as the `source`
 * of a chain item whose card left the board, can never point at a different object) and carry no
 * ordering information (see `state/ids.ts`). Chain items, decisions and battlefields are public and
 * numbered sequentially.
 */
import { invariant } from '../../kernel/errors';
import type { GameState } from '../state/game-state';
import type { BattlefieldId, ChainItemId, DecisionId, ObjectId } from '../state/ids';

/** Size of the object-id space: 6 base-36 characters. */
const ID_SPACE = 36 ** 6;
const ROUNDS = 8;

function mix(x: number): number {
  let h = x >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** A bijection on unsigned 32-bit integers (balanced Feistel network over 16-bit halves). */
function permute32(value: number, key: readonly number[]): number {
  let left = value >>> 16;
  let right = value & 0xffff;
  for (let round = 0; round < ROUNDS; round++) {
    const k = (key[round % key.length] ?? 0) ^ Math.imul(round + 1, 0x9e3779b9);
    const next = (left ^ (mix(right ^ k) & 0xffff)) & 0xffff;
    left = right;
    right = next;
  }
  return ((left << 16) | right) >>> 0;
}

/** Maps a counter in `[0, ID_SPACE)` to a unique value in the same range (cycle walking). */
function permuteIdSpace(counter: number, key: readonly number[]): number {
  let x = counter;
  do {
    x = permute32(x, key);
  } while (x >= ID_SPACE);
  return x;
}

/** A fresh object id, unique for the whole game. */
export function newObjectId(draft: GameState): ObjectId {
  const counter = draft.clock.nextObjectId;
  invariant(counter < ID_SPACE, 'Object id space exhausted');
  draft.clock.nextObjectId = counter + 1;
  const id: ObjectId = `o${permuteIdSpace(counter, draft.rng.ids).toString(36).padStart(6, '0')}`;
  invariant(!(id in draft.objects), `Object id ${id} is already in use`);
  return id;
}

/** The next logical timestamp (layer ordering, CR 480-482). */
export function nextTimestamp(draft: GameState): number {
  const t = draft.clock.nextTimestamp;
  draft.clock.nextTimestamp = t + 1;
  return t;
}

/** The next chain item id. */
export function newChainItemId(draft: GameState): ChainItemId {
  const n = draft.clock.nextChainItem;
  draft.clock.nextChainItem = n + 1;
  return `c${n}`;
}

/** The next decision id. */
export function newDecisionId(draft: GameState): DecisionId {
  const n = draft.clock.nextDecision;
  draft.clock.nextDecision = n + 1;
  return `d${n}`;
}

/** The next battlefield location id. */
export function newBattlefieldId(draft: GameState): BattlefieldId {
  const n = draft.clock.nextBattlefield;
  draft.clock.nextBattlefield = n + 1;
  return `bf${n}`;
}
